import { useEffect, useRef } from 'react';
import { posthog } from '../lib/analytics/posthogClient';
import { supabase } from '../lib/supabase';
import { useUserAuthOptional } from '../contexts/UserAuthContext';
import type { LocationChoice, UserPreferences } from '../contexts/UserContext';
import { italianCities } from '../data/italian-cities';

/**
 * v1 preference synchronisation (legacy lane).
 *
 * MERGE NOTE (origin/main 6cb6898): the v1 lane no longer speaks the
 * revisioned `GET/PUT /api/me/preferences` protocol (that plan's T030 — a
 * different plan's task id, not the canonical-territories T001–T019). It goes
 * back to reading and writing the `users` row directly through Supabase, the
 * way the site did before it: this hook only READS/merges on auth change, and
 * explicit writes are `persistUserPrefs` in `UserContext`. The revisioned
 * endpoint stays alive for the v2 lane alone (`useUserPrefsSyncV2`, our
 * T008-BE wire contract), which is why the service worker still lists it as
 * NetworkOnly.
 *
 * What this file still owns FOR the v2 lane (kept on purpose, not reverted):
 * - the local storage separation (anonymous / per-user / legacy owner-less)
 *   that the v1→v2 conversion reads and that `rewriteV1GpsLocations` rewrites
 *   in place (the GPS-strip security fix);
 * - the shared user-facing copy and the `PrefsSyncNotice` shape.
 */

/**
 * T017: Push the user's preference values to PostHog as person properties.
 * Auto no-op when PostHog is opted out. Safe to call repeatedly.
 *
 * Only fields explicitly present (key in object) are sent. Pass `null` to
 * clear a property; omit a key to leave the existing PostHog value untouched.
 */
export function syncPersonProperties(props: {
  preferred_city?: string | null;
  preferred_categories?: string[] | null;
  preferred_radius_km?: number | null;
}): void {
  const payload: Record<string, string | string[] | number | null> = {};
  if ('preferred_city' in props) payload.preferred_city = props.preferred_city ?? null;
  if ('preferred_categories' in props)
    payload.preferred_categories = props.preferred_categories ?? null;
  if ('preferred_radius_km' in props)
    payload.preferred_radius_km = props.preferred_radius_km ?? null;
  if (Object.keys(payload).length === 0) return;
  posthog.setPersonProperties(payload);
}

/**
 * The single default-radius authority (plan l.69: `radiusKm` is independent of
 * the location union — `none`/`needs_confirmation` keeps a radius without
 * activating geography). Exported for the v2 store/migration (T011-FE).
 */
export const DEFAULT_RADIUS_KM = 25;

export interface PrefsSyncNotice {
  /**
   * - `conflict` / `ambiguous-city` / `rejected`: server outcomes of a
   *   mutation (409 / 422 / 400). Produced by the v2 protocol only now — the
   *   reverted v1 lane has no revision to conflict on.
   * - `migration` (T011-FE): a v1 multi-city list converted to its first
   *   comune.
   * - `needs-confirmation` (T011-FE): the v1 location could not be classified
   *   (homonym, merged comune, ambiguous containment, or the server's
   *   `locationNeedsConfirmation`) — the identity stays NULL and the user is
   *   asked to choose; radius and favorites are kept.
   * - `location-unavailable` (T011-FE): a live GPS placement could not place
   *   the user (unavailable/out of registry/browser failure) — the previous
   *   identity is KEPT untouched and the user is asked to choose manually.
   */
  kind:
    | 'conflict'
    | 'ambiguous-city'
    | 'rejected'
    | 'migration'
    | 'needs-confirmation'
    | 'location-unavailable';
  message: string;
  /** 422 candidates (city + province) so the user can pick the right comune. */
  candidates?: { city: string; province: string }[];
}

/**
 * Shape of the queued intent kept in a per-user store (`pending`). It is now
 * only READ — by the v1→v2 outbox conversion — since the reverted v1 lane no
 * longer queues server intents.
 */
export interface PrefsPatch {
  city?: string | null;
  province?: string | null;
  radiusKm?: number | null;
  favoriteCategories?: string[];
}

/** Shared user-facing copy (reused by the v2 protocol, T011-FE). */
export const CONFLICT_MESSAGE =
  'Le preferenze sono state aggiornate da un altro dispositivo o scheda. La tua modifica non è stata applicata.';
export const AMBIGUOUS_MESSAGE =
  'Comune non univoco: scegli la provincia per completare la modifica.';
export const REJECTED_MESSAGE = 'Modifica non valida: le preferenze non sono state aggiornate.';
// ---------------------------------------------------------------------------
// T030 — storage separation (anonymous / per-user / legacy owner-less)
// ---------------------------------------------------------------------------
//
// - LEGACY_PREFS_KEY ('fieravicino_user_prefs'): the pre-T030 owner-less
//   store, written by anonymous AND logged-in users alike. Provenance is
//   unknown, so it is read for local continuity only and is NEVER attributed
//   to an account ("resta locale").
// - ANON_PREFS_KEY: the current anonymous view, with per-field provenance.
//   Only fields explicitly produced while anonymous (provenance 'anon') are
//   importable into account fields with initialized=false. Legacy residue and
//   already-claimed fields are 'local-only' and never imported.
// - userPrefsStorageKey(userId): per-account local cache + queued offline
//   intents. Never shared between accounts.

export const LEGACY_PREFS_KEY = 'fieravicino_user_prefs';
export const ANON_PREFS_KEY = 'fieravicino_user_prefs:anon';
export const USER_PREFS_KEY_PREFIX = 'fieravicino_user_prefs:user:';

export function userPrefsStorageKey(userId: string): string {
  return USER_PREFS_KEY_PREFIX + userId;
}

/**
 * 'anon' = genuinely produced while anonymous (importable into account fields
 * that were never initialized). 'local-only' = stays local, never attributed
 * to an account (owner-less legacy residue or already claimed at first login).
 */
export type PrefsProvenanceField = 'anon' | 'local-only';

export interface PrefsProvenance {
  location: PrefsProvenanceField;
  favoriteCategories: PrefsProvenanceField;
}

export const ANON_PROVENANCE: PrefsProvenance = {
  location: 'anon',
  favoriteCategories: 'anon',
};

export const LOCAL_ONLY_PROVENANCE: PrefsProvenance = {
  location: 'local-only',
  favoriteCategories: 'local-only',
};

export interface AnonStoredPrefs {
  v: 2;
  prefs: UserPreferences;
  provenance: PrefsProvenance;
}

export interface UserStoredPrefs {
  v: 2;
  prefs: UserPreferences;
  pending: PrefsPatch | null;
}

function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* ignore quota errors */
  }
}

function isPrefsBlob(v: unknown): v is UserPreferences {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return !!o.location && typeof o.location === 'object' && Array.isArray(o.favoriteCategories);
}

function normalizeProvenance(v: unknown): PrefsProvenance {
  const o = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
  const field = (x: unknown): PrefsProvenanceField => (x === 'anon' ? 'anon' : 'local-only');
  return { location: field(o.location), favoriteCategories: field(o.favoriteCategories) };
}

/** Read the current anonymous store. Returns null when absent or malformed. */
export function loadAnonStoredPrefs(): AnonStoredPrefs | null {
  const raw = safeGet(ANON_PREFS_KEY);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const o = parsed as Record<string, unknown>;
    if (o.v !== 2 || !isPrefsBlob(o.prefs)) return null;
    return {
      v: 2,
      prefs: o.prefs as UserPreferences,
      provenance: normalizeProvenance(o.provenance),
    };
  } catch {
    return null;
  }
}

/** Overwrite the anonymous store (full record). */
export function saveAnonStoredPrefs(record: AnonStoredPrefs): void {
  safeSet(ANON_PREFS_KEY, JSON.stringify(record));
}

/**
 * Claim the current anonymous batch for the logged-in account: every field
 * becomes 'local-only' so it can never be imported by a second login.
 */
export function claimAnonStoredPrefs(): void {
  const current = loadAnonStoredPrefs();
  if (!current) return;
  saveAnonStoredPrefs({ v: 2, prefs: current.prefs, provenance: LOCAL_ONLY_PROVENANCE });
}

/** Synchronous read-modify-write on a per-user store record. */
export function updateUserStoredPrefs(
  userId: string,
  updater: (prev: UserStoredPrefs) => UserStoredPrefs
): void {
  const key = userPrefsStorageKey(userId);
  const raw = safeGet(key);
  let prev: UserStoredPrefs = { v: 2, prefs: { location: { type: 'none' }, favoriteCategories: [] }, pending: null };
  if (raw) {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        const o = parsed as Record<string, unknown>;
        if (o.v === 2 && isPrefsBlob(o.prefs)) {
          prev = {
            v: 2,
            prefs: o.prefs as UserPreferences,
            pending: isPatch(o.pending) ? (o.pending as PrefsPatch) : null,
          };
        }
      }
    } catch {
      /* fall through to defaults */
    }
  }
  safeSet(key, JSON.stringify(updater(prev)));
}

/** Read a per-user store record. Returns null when absent or malformed. */
export function loadUserStoredPrefs(userId: string): UserStoredPrefs | null {
  const raw = safeGet(userPrefsStorageKey(userId));
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const o = parsed as Record<string, unknown>;
    if (o.v !== 2 || !isPrefsBlob(o.prefs)) return null;
    return {
      v: 2,
      prefs: o.prefs as UserPreferences,
      pending: isPatch(o.pending) ? (o.pending as PrefsPatch) : null,
    };
  } catch {
    return null;
  }
}

function isPatch(v: unknown): boolean {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

// ---------------------------------------------------------------------------
// v1 server synchronisation — direct `users` row through Supabase (reverted
// from the revisioned /api/me/preferences protocol, see the file header).
// ---------------------------------------------------------------------------

interface DbUserRow {
  city: string | null;
  radius_km: number | null;
  favorite_categories: string[] | null;
}

interface UseUserPrefsSyncOptions {
  preferences: UserPreferences;
  setPreferences: (p: UserPreferences) => void;
  /**
   * T011-FE: false while the discovery gate is in `v2` mode, where the v2
   * protocol (`useUserPrefsSyncV2`) owns the account row. Default true, so
   * every existing caller/test keeps the historical behaviour.
   */
  enabled?: boolean;
}

/**
 * Synchronize user preferences between localStorage and the `users` table.
 *
 * Merge strategy ("DB wins if non-empty, else migrate localStorage -> DB"):
 *  - city: DB.city if non-null. Otherwise migrate from localStorage.location:
 *      type='cities' -> first city name; type='gps' -> derivedCityName (if any).
 *  - favorite_categories: DB array if non-empty. Otherwise migrate from
 *      localStorage.favoriteCategories if non-empty.
 *  - radius_km: DB.radius_km if non-null. Otherwise migrate from localStorage
 *      location.radiusKm. Otherwise default 25 in memory only (no UPDATE).
 *
 * Privacy: GPS coordinates are NEVER persisted to DB. Only city name is.
 *
 * Multi-city note: localStorage supports multiple cities per LocationChoice
 * (`type='cities'`), but the DB has a single `city VARCHAR(100)` column.
 * Trade-off accepted: only the first city is synced cross-device. Multi-city
 * remains a local-only feature.
 *
 * Idempotency: tracks last user.id synced via useRef. Skips re-sync.
 * `enabled: false` (v2 gate open) makes the whole lane inert — no read, no
 * migration write — so exactly one protocol ever speaks for an account row.
 */
export function useUserPrefsSync({
  preferences,
  setPreferences,
  enabled = true,
}: UseUserPrefsSyncOptions): void {
  const { user } = useUserAuthOptional();
  const lastSyncedRef = useRef<string | null>(null);
  // Hold latest preferences in a ref so we don't re-trigger sync on every state change.
  const prefsRef = useRef(preferences);
  prefsRef.current = preferences;

  useEffect(() => {
    if (!enabled || !user) {
      lastSyncedRef.current = null;
      return;
    }
    if (lastSyncedRef.current === user.id) return;

    let cancelled = false;
    const userId = user.id;

    (async () => {
      try {
        const { data, error } = await supabase
          .from('users')
          .select('city, radius_km, favorite_categories')
          .eq('id', userId)
          .single();
        if (error) {
          console.warn('[useUserPrefsSync] select failed:', error.message);
          return;
        }
        if (cancelled) return;

        const dbRow = (data ?? {
          city: null,
          radius_km: null,
          favorite_categories: null,
        }) as DbUserRow;

        const local = prefsRef.current;
        const update: { city?: string; radius_km?: number; favorite_categories?: string[] } = {};

        // ---- city ----
        let mergedCity: string | null = null;
        if (dbRow.city && dbRow.city.trim().length > 0) {
          mergedCity = dbRow.city;
        } else if (local.location.type === 'cities' && local.location.cities.length > 0) {
          mergedCity = local.location.cities[0]!.name;
          update.city = mergedCity;
        } else if (local.location.type === 'gps' && local.location.derivedCityName) {
          mergedCity = local.location.derivedCityName;
          update.city = mergedCity;
        }

        // ---- favorite_categories ----
        let mergedCategories: string[] = [];
        if (Array.isArray(dbRow.favorite_categories) && dbRow.favorite_categories.length > 0) {
          mergedCategories = dbRow.favorite_categories;
        } else if (local.favoriteCategories.length > 0) {
          mergedCategories = local.favoriteCategories;
          update.favorite_categories = mergedCategories;
        }

        // ---- radius_km ----
        let mergedRadius: number = DEFAULT_RADIUS_KM;
        if (typeof dbRow.radius_km === 'number' && Number.isFinite(dbRow.radius_km)) {
          mergedRadius = dbRow.radius_km;
        } else if (
          (local.location.type === 'cities' || local.location.type === 'gps') &&
          Number.isFinite(local.location.radiusKm)
        ) {
          mergedRadius = local.location.radiusKm;
          update.radius_km = mergedRadius;
        }

        // ---- Apply migration UPDATE if any field needs uplift ----
        if (Object.keys(update).length > 0) {
          const { error: updErr } = await supabase.from('users').update(update).eq('id', userId);
          if (updErr) {
            console.warn('[useUserPrefsSync] update failed:', updErr.message);
          }
        }
        if (cancelled) return;

        // ---- Compute merged in-memory location ----
        let mergedLocation: LocationChoice = local.location;
        if (mergedCity) {
          // Look up coordinates from static dataset to reconstruct cities-type LocationChoice.
          const cityEntry = italianCities.find(
            (c) => c.name.toLowerCase() === mergedCity!.toLowerCase()
          );
          if (cityEntry) {
            mergedLocation = {
              type: 'cities',
              cities: [
                {
                  name: cityEntry.name,
                  lat: cityEntry.coordinates[0],
                  lng: cityEntry.coordinates[1],
                },
              ],
              radiusKm: mergedRadius,
            };
          } else if (local.location.type === 'cities' || local.location.type === 'gps') {
            // Preserve current location shape but apply merged radius.
            mergedLocation = { ...local.location, radiusKm: mergedRadius } as LocationChoice;
          }
          // else: name not in dataset and no current location -> leave as-is.
        } else if (
          (local.location.type === 'cities' || local.location.type === 'gps') &&
          mergedRadius !== local.location.radiusKm
        ) {
          mergedLocation = { ...local.location, radiusKm: mergedRadius } as LocationChoice;
        }

        // ---- Update in-memory state ----
        setPreferences({
          ...local,
          location: mergedLocation,
          favoriteCategories: mergedCategories,
        });

        // ---- Mark as synced ----
        lastSyncedRef.current = userId;

        // T017: push merged prefs to PostHog person properties.
        syncPersonProperties({
          preferred_city: mergedCity,
          preferred_categories: mergedCategories.length > 0 ? mergedCategories : null,
          preferred_radius_km: Number.isFinite(mergedRadius) ? mergedRadius : null,
        });
      } catch (err) {
        console.warn('[useUserPrefsSync] unexpected error:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
    // setPreferences is stable; `enabled` flips with the discovery gate, so it
    // is listed to make the v2 lane take over the moment the gate opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, enabled]);
}
